import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { WBSButton } from '@/common/WBSButton';
import { CancelPendingRequestPanel } from '@/follow-up/CancelPendingRequestPanel';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import { GroupFollowUpRequestDetail } from '@/follow-up/GroupFollowUpRequestDetail';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/pending-requests/view/$requestId',
)({
  component: ViewPendingRequestScreen,
});

/**
 * Subnav 2's request detail.
 *
 * One renderer: legacy's separate view for a student's self-request can't
 * occur, since a request's creator is always a study helper.
 *
 * A `rejected` request reaches this page normally: the list loads rows while
 * they're pending, and the student may reject before the detail loads. The
 * rejection reason and the Dismiss button are for that case.
 */
function ViewPendingRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName, requestId } = Route.useParams();
  const { setCanceledRequestId } = useFollowUpsAlert();

  const [dismissed, setDismissed] = useState(false);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getGroupFollowUpRequest.useQuery(
    { path: { group_id: groupId ?? 0, request_id: Number(requestId) } },
    { enabled: groupId !== undefined },
  );

  const {
    mutateAsync: cancelRequest,
    isPending: cancelling,
    error: cancelError,
  } = api.cancelGroupFollowUpRequest.useMutation();
  const {
    mutateAsync: dismissRejection,
    isPending: dismissing,
    error: dismissError,
  } = api.dismissGroupFollowUpRequest.useMutation();

  const backToSubnav = () =>
    void navigate({
      to: '/groups/$groupName/follow-ups/pending-requests/',
      params: { groupName },
    });

  const handleCancel = async (reason: string): Promise<void> => {
    if (groupId === undefined) return;
    try {
      await cancelRequest({
        path: { group_id: groupId, request_id: Number(requestId) },
        body: { reason },
      });
    } catch {
      // Rendered from `cancelError`. The request keeps its status, so the panel
      // stays open with the typed reason still in it.
      return;
    }
    setCanceledRequestId(requestId);
    // Both lists change: the row leaves the pending list and joins subnav 1,
    // which shows canceled requests by default.
    await Promise.all([
      api.listGroupPendingFollowUpRequests.invalidateQueries(),
      api.listGroupFollowUpRequests.invalidateQueries(),
    ]);
    backToSubnav();
  };

  const handleDismiss = async (): Promise<void> => {
    if (groupId === undefined) return;
    try {
      await dismissRejection({
        path: { group_id: groupId, request_id: Number(requestId) },
      });
    } catch {
      // Rendered from `dismissError`.
      return;
    }
    // Stays put rather than redirecting to the pending list as legacy does: the
    // dismissed request is `rejected`, so that list never contained it.
    //
    // Nothing is invalidated: no v3 read returns the dismissed flag, so a
    // refetch would return the same payload, and `can_dismiss` doesn't change,
    // so `dismissed` retires the button.
    setDismissed(true);
  };

  // 404 for a request outside the group's roster, the same screen as an unknown
  // id.
  if (isError) return <NotFoundScreen />;
  if (isLoading || detail === undefined) return null;

  const writeError = cancelError ?? dismissError;

  return (
    <GroupFollowUpRequestDetail detail={detail} onBack={backToSubnav}>
      {dismissed && (
        <AlertBox variant="success">
          {t('wbs.follow-up-request-rejection-dismissed')}
        </AlertBox>
      )}
      {writeError != null && (
        <AlertBox variant="error">
          {/* Translation Needed */}
          {errorMessage(
            writeError,
            t('wbs.there-was-a-problem-with-your-submission'),
          )}
        </AlertBox>
      )}

      {/* Both blocks depend on status and, unlike subnav 1's detail, have
         no "No message" fallback, as in legacy. An empty box is common
         here. */}
      {detail.status === 'pending' && (
        <FollowUpNotes
          // "if Accepted", a different catalogue string from subnav 1's "when
          // Accepted".
          title={t('wbs.your-note-to-the-2')}
          message={detail.message_for_follow_up_coordinator}
        />
      )}
      {detail.status === 'rejected' && (
        <FollowUpNotes
          title={t('lessons.student-s-reason-for')}
          message={detail.message_for_study_helper_reject}
        />
      )}

      {/* `can_cancel` and `can_dismiss` come from the server: each combines
         the authorization (the student's assigned study helper, not the
         creator or a group admin) with the status. Re-deriving them here
         risks showing Cancel on a row the write then 409s. */}
      {detail.can_cancel && (
        <CancelPendingRequestPanel
          onConfirm={(reason) => void handleCancel(reason)}
          submitting={cancelling}
        />
      )}
      {detail.can_dismiss && !dismissed && (
        <div>
          <WBSButton
            wide
            type="button"
            onClick={() => void handleDismiss()}
            isLoading={dismissing}
          >
            {t('wbs.dismiss')}
          </WBSButton>
        </div>
      )}
    </GroupFollowUpRequestDetail>
  );
}
